<script setup>
import { useAnalysisStore } from '@/store/modules/analysis';

const fullWidthMode = inject('fullWidthMode', false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
const store = useAnalysisStore();

import ImgCatHanging from '@manseImages/yongsin-light/img-yongsin-cat-hanging.png';
import CircleYongsinRaw from '@manseImages/yongsin-light/img-circle-yongsin.svg?raw';
import CircleHeesinRaw from '@manseImages/yongsin-light/img-circle-heesin.svg?raw';

// 오른쪽 페이지 에셋
import IconOakTree from '@manseImages/yongsin-light/icon-oak-tree.png';
import IconFire from '@manseImages/yongsin-light/icon-fire.png';
import IconMountain from '@manseImages/yongsin-light/icon-mountain.png';
import IconBattle from '@manseImages/yongsin-light/icon-battle.png';
import IconWater from '@manseImages/yongsin-light/icon-water.png';
import ImgCatPointer from '@manseImages/yongsin-light/img-yongsin-cat-pointer.png';
import ImgCatCheerup from '@manseImages/yongsin-light/img-yongsin-cat-cheerup.png';
import ArrowYongsin from '@manseImages/yongsin-light/arrow-yongsin.svg?url';
import ArrowHeesin from '@manseImages/yongsin-light/arrow-heesin.svg?url';

const props = defineProps({
  isMobile: Boolean,
  contentData: {
    type: Object,
    default: null,
  },
});

// 사용자 이름
const userName = computed(() => store.userName || '-');

// 칩 라벨
const chipLabel = computed(() => props.contentData?.chipLabel ?? '');

// 핵심 문구
const energyTypeTitle = computed(() => props.contentData?.energyTypeTitle ?? '');

// 설명 문단
const descriptions = computed(() => props.contentData?.descriptions ?? []);

// 오행 일러스트 데이터 (고정 레이아웃, Figma 좌표 기반)
const FIVE_ELEMENTS = [
  { name: '목', color: '#5e9f1c', lineLeft: 15, lineH: 131, charLeft: 0, charTop: 124, rotate: -4.61 },
  { name: '화', color: '#c23535', lineLeft: 71, lineH: 99, charLeft: 59, charTop: 87, rotate: 5 },
  { name: '토', color: '#db9823', lineLeft: 156, lineH: 112, charLeft: 143, charTop: 102, rotate: 5 },
  { name: '금', color: '#949494', lineLeft: 236, lineH: 95, charLeft: 225, charTop: 87, rotate: -1 },
  { name: '수', color: '#404040', lineLeft: 299, lineH: 133, charLeft: 285, charTop: 125, rotate: -3 },
];

// 오행 색상 맵
const ELEMENT_COLOR_MAP = {
  '목': '#5e9f1c',
  '화': '#c23535',
  '토': '#db9823',
  '금': '#949494',
  '수': '#404040',
};

// 오행별 연한 배경색 (포스트잇, 말풍선)
const ELEMENT_LIGHT_BG = {
  '목': '#edf7e4',
  '화': '#fde8e8',
  '토': '#fff6ed',
  '금': '#f0f2f5',
  '수': '#ececec',
};

// 용신·희신 하이라이트 인덱스
const highlightedIndices = computed(() => props.contentData?.highlightedIndices ?? []);

// 글자 위치 기반으로 원 위치 계산 (글자 중앙에 원이 오도록)
// 용신 원: 55x65, 희신 원: 51x55
const YONGSIN_CIRCLE = { w: 68, h: 59, innerW: 54, innerH: 63, rotate: 85 };
const HEESIN_CIRCLE = { w: 59, h: 55, innerW: 50, innerH: 54, rotate: 84 };

const calcCirclePos = (elIdx, circle) => {
  const el = FIVE_ELEMENTS[elIdx];
  if (!el) return { left: 0, top: 0, width: circle.w, height: circle.h };
  // 글자 크기 약 30px, 글자 중앙에 원 중앙을 맞춤 (세로 +5px 보정)
  const charCenterX = el.charLeft + 15;
  const charCenterY = el.charTop + 20;
  return {
    left: charCenterX - circle.w / 2,
    top: charCenterY - circle.h / 2,
    width: circle.w,
    height: circle.h,
  };
};

// SVG fill 색상을 동적으로 교체 (fill="none" 제외, 실제 색상만 교체)
const replaceSvgFill = (svgRaw, newColor) => {
  return svgRaw.replace(/fill="#[0-9A-Fa-f]{6}"/, `fill="${newColor}"`);
};

const circleSvgYongsin = computed(() => replaceSvgFill(CircleYongsinRaw, yongsinColor.value));
const circleSvgHeesin = computed(() => replaceSvgFill(CircleHeesinRaw, heesinColor.value));

// 동적 원 데이터 (위치 + SVG)
const dynamicCircles = computed(() => {
  const indices = highlightedIndices.value;
  return [
    {
      svg: circleSvgYongsin.value,
      pos: calcCirclePos(indices[0], YONGSIN_CIRCLE),
      rotate: YONGSIN_CIRCLE.rotate,
      innerW: YONGSIN_CIRCLE.innerW,
      innerH: YONGSIN_CIRCLE.innerH,
    },
    {
      svg: circleSvgHeesin.value,
      pos: calcCirclePos(indices[1], HEESIN_CIRCLE),
      rotate: HEESIN_CIRCLE.rotate,
      innerW: HEESIN_CIRCLE.innerW,
      innerH: HEESIN_CIRCLE.innerH,
    },
  ];
});

// ═══ 오른쪽 페이지 데이터 ═══

// 오행 아이콘 맵
const ELEMENT_ICON_MAP = {
  '목': IconOakTree,
  '화': IconFire,
  '토': IconMountain,
  '금': IconBattle,
  '수': IconWater,
};

// 용신 정보
const yongsinInfo = computed(() => props.contentData?.yongsinInfo ?? { name: '', element: '', keyword: '' });

// 희신 정보
const heesinInfo = computed(() => props.contentData?.heesinInfo ?? { name: '', element: '', keyword: '' });

// 용신 아이콘
const yongsinIcon = computed(() => ELEMENT_ICON_MAP[yongsinInfo.value.element] ?? IconMountain);

// 희신 아이콘
const heesinIcon = computed(() => ELEMENT_ICON_MAP[heesinInfo.value.element] ?? IconBattle);

// 용신 색상
const yongsinColor = computed(() => ELEMENT_COLOR_MAP[yongsinInfo.value.element] ?? '');

// 희신 색상
const heesinColor = computed(() => ELEMENT_COLOR_MAP[heesinInfo.value.element] ?? '');

// 용신/희신 연한 배경색
const yongsinLightBg = computed(() => ELEMENT_LIGHT_BG[yongsinInfo.value.element] ?? '#fff6ed');
const heesinLightBg = computed(() => ELEMENT_LIGHT_BG[heesinInfo.value.element] ?? '#f0f2f5');

// AI 모델 신뢰도
const aiModelReliability = computed(() => props.contentData?.aiModelReliability ?? '');

// 말풍선 텍스트
const speechLines = computed(() => props.contentData?.speechLines ?? []);

// 말풍선 하단 조언
const speechAdvice = computed(() => props.contentData?.speechAdvice ?? '');

// 하단 카드 텍스트
const bottomCardIntro = computed(() => props.contentData?.bottomCardIntro ?? []);

const bottomCardQuote = computed(() => props.contentData?.bottomCardQuote ?? '');

const bottomCardOutro = computed(() => props.contentData?.bottomCardOutro ?? []);
</script>

<template>
  <VRow class="full-height">
    <!-- ═══ 왼쪽 페이지 ═══ -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="ysl-card full-height right">
        <!-- 타이틀 바 -->
        <div class="ysl-title-bar">
          <span class="ysl-title-bar__num">1</span>
          <span class="ysl-title-bar__text">내 사주에서 나를 돕는 기운!</span>
        </div>

        <!-- 본문 영역 -->
        <div class="ysl-content">
          <div class="ysl02-body">
            <!-- 상단 텍스트 섹션 -->
            <div class="ysl02-text-section">
              <!-- 칩 뱃지 -->
              <div class="ysl02-chip">{{ chipLabel }}</div>

              <!-- 메인 타이틀 -->
              <div class="ysl02-main-title">
                <p>{{ userName }} 님은</p>
                <p class="ysl02-main-title__highlight">'{{ energyTypeTitle }}'</p>
                <p>
                  <span class="ysl02-main-title__highlight">가장 힘이 살아나는 타입 </span>
                  <span>입니다.</span>
                </p>
              </div>

              <!-- 설명 문단 -->
              <div class="ysl02-desc">
                <div
                  v-for="(paragraph, pi) in descriptions"
                  :key="pi"
                  class="ysl02-desc__block"
                >
                  <p v-for="(line, li) in paragraph" :key="li">{{ line }}</p>
                </div>
              </div>
            </div>

            <!-- AI 모델 분석 신뢰도 배지 -->
            <div v-if="aiModelReliability" class="ysl02-ai-badge">
              {{ aiModelReliability }}
            </div>

            <!-- 오행 일러스트 (행잉 모빌) -->
            <div class="ysl02-hanging">
              <!-- 줄 + 글자 -->
              <template v-for="(el, idx) in FIVE_ELEMENTS" :key="el.name">
                <div
                  class="ysl02-hanging__line"
                  :style="{
                    left: el.lineLeft + 'px',
                    height: el.lineH + 'px',
                    background: el.color,
                  }"
                />
                <span
                  class="ysl02-hanging__char"
                  :style="{
                    left: el.charLeft + 'px',
                    top: el.charTop + 'px',
                    color: el.color,
                    transform: `rotate(${el.rotate}deg)`,
                  }"
                >
                  {{ el.name }}
                </span>
              </template>

              <!-- 용신·희신 하이라이트 원 (동적 SVG) -->
              <div
                v-for="(circle, ci) in dynamicCircles"
                :key="'circle-' + ci"
                class="ysl02-hanging__circle-wrap"
                :style="{
                  left: circle.pos.left + 'px',
                  top: circle.pos.top + 'px',
                  width: circle.pos.width + 'px',
                  height: circle.pos.height + 'px',
                  '--draw-delay': (0.3 + ci * 0.7) + 's',
                }"
              >
                <div
                  class="ysl02-hanging__circle-img is-drawing"
                  :style="{
                    width: circle.innerW + 'px',
                    height: circle.innerH + 'px',
                    transform: `rotate(${circle.rotate}deg)`,
                  }"
                  v-html="circle.svg"
                />
              </div>

              <!-- 고양이 캐릭터 -->
              <img
                :src="ImgCatHanging"
                alt=""
                class="ysl02-hanging__cat"
              />
            </div>
          </div>
        </div>
      </VCard>
    </VCol>

    <!-- ═══ 오른쪽 페이지 ═══ -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="ysl-card full-height">
        <div class="ysl-title-bar" :class="{ 'ysl-title-bar--text-hidden': !isMobile }">
          <span class="ysl-title-bar__num">1</span>
          <span class="ysl-title-bar__text">내 사주에서 나를 돕는 기운!</span>
        </div>

        <div class="ysl-content">
          <div class="ysl02-right">
            <!-- ── 1) 용신·희신 카드 영역 (Figma 좌표 기반) ── -->
            <div class="ysl02-cards-area">
              <!-- 왼쪽 라벨 -->
              <div class="ysl02-label">
                <img :src="ArrowYongsin" alt="" class="ysl02-label__arrow ysl02-label__arrow--r" />
                <span class="ysl02-label__text">나에게 힘을<br />주는 기운<br />(용신)</span>
              </div>

              <!-- 용신 카드 -->
              <div
                class="ysl02-note ysl02-note--yongsin"
                :style="{
                  background: yongsinLightBg,
                  '--kw-border-color': yongsinColor,
                }"
              >
                <img :src="yongsinIcon" alt="" class="ysl02-note__icon" />
                <p class="ysl02-note__name ysl02-note__name--lg">{{ yongsinInfo.name }}</p>
                <span class="ysl02-note__kw ysl02-note__kw--yongsin" :style="{ color: yongsinColor }">{{ yongsinInfo.keyword }}</span>
              </div>

              <!-- 희신 카드 -->
              <div
                class="ysl02-note ysl02-note--heesin"
                :style="{
                  background: heesinLightBg,
                  '--kw-border-color': heesinColor,
                }"
              >
                <img :src="heesinIcon" alt="" class="ysl02-note__icon" />
                <p class="ysl02-note__name">{{ heesinInfo.name }}</p>
                <span class="ysl02-note__kw ysl02-note__kw--heesin" :style="{ color: heesinColor }">{{ heesinInfo.keyword }}</span>
              </div>

              <!-- 오른쪽 라벨 -->
              <div class="ysl02-label">
                <img :src="ArrowHeesin" alt="" class="ysl02-label__arrow ysl02-label__arrow--l" />
                <span class="ysl02-label__text">나를<br />보좌해주는 기운<br />(희신)</span>
              </div>
            </div>

            <!-- ── 2) 말풍선 영역 ── -->
            <div class="ysl02-speech-area">
              <!-- 고양이 캐릭터 -->
              <img :src="ImgCatPointer" alt="" class="ysl02-speech-area__cat" />

              <!-- 말풍선 -->
              <div class="ysl02-bubble-wrap">
                <div class="ysl02-bubble-arrow" :style="{ borderRightColor: yongsinLightBg }" />
                <div class="ysl02-bubble" :style="{ background: yongsinLightBg }">
                  <div class="ysl02-bubble__main">
                    <p v-for="(line, idx) in speechLines" :key="idx">
                      <span
                        class="ysl02-bubble__prefix"
                        :style="{ color: line.prefixType === 'yongsin' ? yongsinColor : heesinColor }"
                      >{{ line.prefix }}</span><span>{{ line.suffix }}</span>
                    </p>
                  </div>
                  <p v-if="speechAdvice" class="ysl02-bubble__advice">{{ speechAdvice }}</p>
                </div>
              </div>
            </div>

            <!-- ── 3) 하단 요약 카드 ── -->
            <div class="ysl02-summary-card">
              <!-- 소개 텍스트 -->
              <div class="ysl02-summary-card__intro">
                <p v-for="(line, idx) in bottomCardIntro" :key="idx">{{ line }}</p>
              </div>

              <!-- 구분선 -->
              <div class="ysl02-summary-card__divider" />

              <!-- 인용 문구 (content1과 동일한 따옴표 SVG) -->
              <div class="ysl02-summary-card__quote-section">
                <div class="ysl02-summary-card__quote-row">
                  <svg class="ysl02-quote-mark left" width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.2 0L4.32 7H7.2V14H0V7L4.32 0H7.2ZM18 0L15.12 7H18V14H10.8V7L15.12 0H18Z" fill="#4E80D6"/>
                  </svg>
                  <p class="ysl02-summary-card__quote-text">{{ bottomCardQuote }}</p>
                  <svg class="ysl02-quote-mark right" width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10.8 14L13.68 7H10.8V0H18V7L13.68 14H10.8ZM0 14L2.88 7H0V0H7.2V7L2.88 14H0Z" fill="#4E80D6"/>
                  </svg>
                </div>

                <!-- 결론 문구 -->
                <div class="ysl02-summary-card__outro">
                  <p v-for="(line, idx) in bottomCardOutro" :key="idx">{{ line }}</p>
                </div>
              </div>

              <!-- 캐릭터 장식 (카드 오른쪽 아래 바깥으로 빠져나옴) -->
              <img :src="ImgCatCheerup" alt="" class="ysl02-summary-card__deco" />
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
$ysl-primary: #4e80d6;
$ysl-primary-light: rgba(78, 128, 214, 0.05);
$ysl-text: #333;

// ── 공통 (content1과 동일 구조) ──
.ysl-card {
  background: #fff;
  border-radius: 0 !important;
  box-shadow: none !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.right {
    border-right: 1px solid #ebebeb;
  }
}

// ── 타이틀 바 (번호 뱃지 스타일) ──
.ysl-title-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 24px;
  background: $ysl-primary-light;
  flex-shrink: 0;

  &__num {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 11px;
    background: #4b73b2;
    color: #fff;
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    flex-shrink: 0;
  }

  &__text {
    color: #4b73b2;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }

  &--text-hidden {
    .ysl-title-bar__num,
    .ysl-title-bar__text {
      visibility: hidden;
    }
  }
}

// ── 본문 영역 ──
.ysl-content {
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 24px;
  padding-top: 48px;
  padding-bottom: 100px;
  display: flex;
  flex-direction: column;
}

// ── content2 바디 ──
.ysl02-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 36px;
}

// ── 텍스트 섹션 ──
.ysl02-text-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

// ── 칩 뱃지 ──
.ysl02-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 16px;
  background: rgba(78, 128, 214, 0.1);
  color: #4b73b2;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  white-space: nowrap;
}

// ── 메인 타이틀 ──
.ysl02-main-title {
  text-align: center;
  color: $ysl-text;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 700;
  line-height: 30px;
  width: 100%;
  max-width: 552px;

  p {
    margin: 0;
  }

  &__highlight {
    color: #5d81bf;
  }
}

// ── 설명 문단 ──
.ysl02-desc {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  width: 100%;
  max-width: 552px;
  text-align: center;
  color: $ysl-text;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.4;

  &__block {
    width: 100%;

    p {
      margin: 0;
    }
  }
}

// ── AI 모델 분석 신뢰도 배지 ──
.ysl02-ai-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 16px;
  background: #f6f8fd;
  border: 1px solid rgba(78, 128, 214, 0.5);
  color: $ysl-text;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
}

// ── 오행 행잉 일러스트 ──
.ysl02-hanging {
  position: relative;
  width: 313px;
  max-width: 100%;
  height: 280px;
  flex-shrink: 0;

  @media (max-width: 400px) {
    transform: scale(0.85);
    transform-origin: top center;
    height: calc(280px * 0.85);
  }

  &__line {
    position: absolute;
    top: 0;
    width: 1px;
    opacity: 0.5;
  }

  &__char {
    position: absolute;
    font-family: Pretendard;
    font-weight: 900;
    font-size: 30px;
    line-height: 1.2;
    white-space: nowrap;
  }

  &__circle-wrap {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  &__circle-img {
    display: block;

    :deep(svg) {
      width: 100%;
      height: 100%;
    }

    &.is-drawing {
      // 원이 그려지는 시작 각도 (왼쪽 아래에서 살짝 오른쪽)
      -webkit-mask: conic-gradient(from 108deg, #000 var(--draw-progress, 0%), transparent 0);
      mask: conic-gradient(from 108deg, #000 var(--draw-progress, 0%), transparent 0);
      animation: ysl02-circle-draw 0.9s cubic-bezier(0.55, 0.1, 0.35, 1) var(--draw-delay, 0s) both;
    }
  }

  &__cat {
    position: absolute;
    left: 83px;
    top: 135px;
    width: 160px;
    height: auto;
    object-fit: contain;
  }
}

// ═══════════════════════════════
// 오른쪽 페이지 스타일
// ═══════════════════════════════

.ysl02-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 36px;
  padding: 12px 0;
  container-type: inline-size;
}

// ── 1) 용신·희신 카드 영역 ──
.ysl02-cards-area {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  flex-wrap: nowrap;

  @container (max-width: 460px) {
    zoom: 0.92;
    gap: 6px;
  }

  @container (max-width: 420px) {
    zoom: 0.85;
    gap: 4px;

    .ysl02-note__name {
      font-size: 15px;
      line-height: 21px;

      &--lg {
        font-size: 18px;
        line-height: 24px;
      }
    }

    .ysl02-note__kw {
      font-size: 13px;
      line-height: 22px;

      &--yongsin {
        font-size: 14px;
      }
    }
  }

  @container (max-width: 380px) {
    zoom: 0.76;
    gap: 4px;

    .ysl02-note__name {
      font-size: 14px;
      line-height: 20px;

      &--lg {
        font-size: 17px;
        line-height: 23px;
      }
    }

    .ysl02-note__kw {
      font-size: 12px;
      line-height: 20px;

      &--yongsin {
        font-size: 13px;
      }
    }
  }

  @container (max-width: 340px) {
    zoom: 0.68;
    gap: 2px;

    .ysl02-note__name {
      font-size: 13px;
      line-height: 19px;

      &--lg {
        font-size: 16px;
        line-height: 22px;
      }
    }

    .ysl02-note__kw {
      font-size: 12px;
      line-height: 20px;

      &--yongsin {
        font-size: 12px;
      }
    }
  }

  @container (max-width: 320px) {
    zoom: 0.6;
    gap: 2px;
  }
}

// 좌우 라벨 (포스트잇 세로 중간에 위치)
.ysl02-label {
  flex-shrink: 0;
  position: relative;

  &__arrow {
    position: absolute;
    width: 28px;
    height: 11px;

    &--r {
      top: -14px;
      right: -2px;
    }

    &--l {
      top: -14px;
      left: -2px;
      transform: scaleX(-1);
    }
  }

  &__text {
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
    color: $ysl-text;
    text-align: center;
    display: block;
  }
}

// 포스트잇 카드
.ysl02-note {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 3px;
  position: relative;
  flex-shrink: 0;

  &__icon {
    width: 48px;
    height: 48px;
    object-fit: contain;
    position: absolute;
    top: -28px;
    z-index: 3;
  }

  &--yongsin &__icon {
    left: -4px;
    transform: rotate(-6deg);
  }

  &--heesin &__icon {
    right: -4px;
    transform: rotate(7deg);
  }

  &__name {
    margin: 0;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    color: $ysl-text;

    &--lg {
      font-size: 20px;
      font-weight: 700;
      line-height: 26px;
    }
  }

  &__kw {
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    line-height: 24px;
    position: relative;
    white-space: nowrap;
    z-index: 1;
    display: inline-block;
    padding: 0 4px;

    &--yongsin {
      font-size: 15px;

      &::before {
        content: '';
        position: absolute;
        inset: -4px -14px;
        border: 1.8px solid var(--kw-border-color, #db9823);
        border-radius: 50%;
        pointer-events: none;
        transform: rotate(-3deg);
      }
    }

    &--heesin {
      &::before {
        content: '';
        position: absolute;
        inset: -3px -10px;
        border: 1.5px solid var(--kw-border-color, #949494);
        border-radius: 50%;
        pointer-events: none;
        transform: rotate(2deg);
      }
    }
  }

  // 용신 카드 (정사각형에 가깝게, aspect-ratio 사용)
  &--yongsin {
    width: 160px;
    aspect-ratio: 1 / 1.05;
    box-shadow: 4px 6px 12px rgba(0, 0, 0, 0.18);
    transform: rotate(-2deg);
    transform-origin: 50% 0;
    z-index: 2;
    padding: 24px 16px 16px;
    animation: ysl02-note-yongsin-drop 1s cubic-bezier(0.34, 1.45, 0.64, 1) 0.4s backwards;

    .ysl02-note__icon {
      animation: ysl02-note-yongsin-icon 0.6s ease-out 1.25s backwards;
    }

    .ysl02-note__kw--yongsin::before {
      animation: ysl02-note-yongsin-kw 0.5s ease-out 1.45s backwards;
    }
  }

  // 희신 카드 (정사각형)
  &--heesin {
    width: 140px;
    aspect-ratio: 1;
    box-shadow: 4px 6px 12px rgba(0, 0, 0, 0.18);
    transform: rotate(3deg);
    z-index: 1;
    padding: 20px 14px 16px;
  }
}

// ── 2) 말풍선 영역 ──
.ysl02-speech-area {
  display: flex;
  align-items: flex-end;
  width: 100%;
  max-width: 552px;
  position: relative;
  padding-right: 20px;

  &__cat {
    width: 138px;
    height: 140px;
    object-fit: contain;
    flex-shrink: 0;
    transform: scaleX(-1);
    margin-right: -20px;
    z-index: 1;

    @media (max-width: 480px) {
      width: 90px;
      height: 92px;
      margin-right: -10px;
    }
  }
}

.ysl02-bubble-wrap {
  position: relative;
  display: flex;
  align-items: center;
  z-index: 2;
  flex: 1;
  min-width: 0;
}

.ysl02-bubble-arrow {
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 12px solid #fff6ed;
  flex-shrink: 0;
  margin-top: 30px;
  filter: drop-shadow(-1px 0 1px rgba(0, 0, 0, 0.04));
}

.ysl02-bubble {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 20px;
  background: #fff6ed;
  border-radius: 12px;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.15);
  flex: 1;
  min-width: 0;

  &__main {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: $ysl-text;

    p {
      margin: 0;
    }
  }

  &__prefix {
    font-weight: 600;
  }

  &__advice {
    margin: 0;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: $ysl-text;
  }
}

// ── 3) 하단 요약 카드 ──
.ysl02-summary-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 552px;
  padding: 40px 20px;
  background: #f6f8fd;
  box-shadow: 4px 4px 4px rgba(0, 0, 0, 0.25);
  position: relative;

  &__intro {
    text-align: center;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
    color: $ysl-text;
    padding: 3px 0;

    p {
      margin: 0;
    }
  }

  &__divider {
    width: 300px;
    height: 1px;
    background: #c2c2c2;
  }

  &__quote-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  &__quote-row {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 5px;
  }

  &__quote-text {
    margin: 0;
    font-family: Pretendard;
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    color: $ysl-text;
    text-align: center;
    word-break: keep-all;
  }

  &__outro {
    text-align: center;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    line-height: 24px;
    color: $ysl-text;

    p {
      margin: 0;
    }
  }

  &__deco {
    position: absolute;
    right: -16px;
    bottom: -50px;
    width: 110px;
    height: auto;
    object-fit: contain;
    pointer-events: none;
  }
}

// 따옴표 (content1과 동일, 파란 계열)
.ysl02-quote-mark {
  flex-shrink: 0;
  width: 18px;
  height: 14px;
  margin-top: 5px;
}

// ── 원 그려지는 애니메이션 (목화토금수 위 동그라미) ──
@property --draw-progress {
  syntax: '<percentage>';
  inherits: true;
  initial-value: 0%;
}

@keyframes ysl02-circle-draw {
  from {
    --draw-progress: 0%;
  }
  to {
    --draw-progress: 100%;
  }
}

// ── 용신 포스트잇 진입 애니메이션 ──
@keyframes ysl02-note-yongsin-drop {
  0% {
    transform: translateY(-120px) rotate(-18deg);
    opacity: 0;
  }
  40% {
    opacity: 1;
  }
  70% {
    transform: translateY(6px) rotate(1deg);
  }
  85% {
    transform: translateY(-2px) rotate(-3deg);
  }
  100% {
    transform: translateY(0) rotate(-2deg);
    opacity: 1;
  }
}

@keyframes ysl02-note-yongsin-icon {
  0% {
    transform: rotate(40deg) translateY(-14px);
    opacity: 0;
  }
  100% {
    transform: rotate(-6deg) translateY(0);
    opacity: 1;
  }
}

@keyframes ysl02-note-yongsin-kw {
  0% {
    opacity: 0;
    transform: rotate(-3deg) scale(0.5);
  }
  100% {
    opacity: 1;
    transform: rotate(-3deg) scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ysl02-hanging__circle-img.is-drawing {
    animation: none;
    -webkit-mask: none;
    mask: none;
  }

  .ysl02-note--yongsin,
  .ysl02-note--yongsin .ysl02-note__icon,
  .ysl02-note--yongsin .ysl02-note__kw--yongsin::before {
    animation: none;
  }
}
</style>
